import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import { siteInfo } from '@/mocks/siteInfo';
import { categories } from '@/mocks/categories';
import { chapters } from '@/mocks/chapters';

const latestChapters = chapters.filter((ch) => ch.isNew).slice(0, 3);
const hotChapters = chapters.filter((ch) => ch.isHot).slice(0, 6);

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteInfo.name,
  url: 'https://naptevq.cn/',
  description: siteInfo.description,
  inLanguage: 'zh-CN',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://naptevq.cn/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

export default function Home() {
  return (
    <>
      <SEO
        title={`${siteInfo.name} - 在线阅读 | 都市情感小说经典之作`}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        schema={homeSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20luxurious%20mansion%20interior%20with%20warm%20golden%20chandelier%20light%2C%20deep%20crimson%20velvet%20curtains%2C%20ornate%20furniture%2C%20mysterious%20atmosphere%2C%20cinematic%20lighting%20with%20dramatic%20shadows%2C%20dark%20academia%20aesthetic%2C%20rich%20textures%2C%20warm%20amber%20and%20burgundy%20tones%2C%20no%20people%2C%20atmospheric%20interior%20design&width=1920&height=1080&seq=hero-bg-2024-x1&orientation=landscape"
            alt="艳母的荒唐赌约 - 豪门故事背景"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 tracking-wide">
              {siteInfo.name}
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto leading-relaxed mb-8">
              {siteInfo.tagline}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={`/article/${chapters[0].slug}`}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                开始阅读
              </Link>
              <Link
                to={`/category/${categories[0].slug}`}
                className="px-8 py-3 border border-white/50 hover:border-white text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                浏览目录
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-white/70 text-xs">
              <span>{siteInfo.status}</span>
              <span className="w-1 h-1 rounded-full bg-white/50"></span>
              <span>共 {siteInfo.totalChapters} 章</span>
              <span className="w-1 h-1 rounded-full bg-white/50"></span>
              <span>{siteInfo.totalWords}</span>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                故事篇章
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                五大篇章，层层递进，带你走进这场荒诞而深刻的情感之旅
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat, idx) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group relative bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="inline-block text-xs text-white/80 bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full mb-2">
                      第 {idx + 1} 篇 · {cat.chapterCount}章
                    </span>
                    <h3 className="text-lg font-heading font-bold text-white mb-1">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-white/70 leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates Section */}
        <section className="py-16 md:py-20 bg-background-100/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-600">刚刚发布的最新章节</p>
              </div>
              <Link
                to={`/category/${categories[4].slug}`}
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {latestChapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/article/${ch.slug}`}
                  className="group bg-background-50 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block text-xs font-medium text-white bg-accent-500 px-2.5 py-1 rounded-full">
                      新
                    </span>
                    <span className="text-xs text-foreground-500">{ch.categoryName}</span>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {ch.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3 mb-4">
                    {ch.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{ch.publishDate}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {(ch.viewCount / 10000).toFixed(1)}万
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link
                to={`/category/${categories[4].slug}`}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Hot Chapters Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                热门章节
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                读者最爱的精彩篇章，每一章都值得细品
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {hotChapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/article/${ch.slug}`}
                  className="group flex gap-4 bg-background-100 rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-heading font-bold text-secondary-400">
                      {ch.chapterNumber}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs text-foreground-500 bg-secondary-100 px-2 py-0.5 rounded-full">
                        {ch.categoryName}
                      </span>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1.5">
                      {ch.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-2">
                      {ch.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {(ch.viewCount / 10000).toFixed(1)}万
                      </span>
                      <span>{ch.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats / CTA Section */}
        <section className="py-16 md:py-20 bg-secondary-100/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="bg-background-50 rounded-2xl p-10 md:p-14 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                开启你的阅读之旅
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto mb-8">
                从第一章开始，见证林婉儿在命运棋局中的蜕变与成长
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
                <div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600">
                    {siteInfo.totalChapters}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">精彩章节</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-accent-600">
                    {siteInfo.totalWords}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">总字数</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600">
                    21万+
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">累计阅读</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-accent-600">
                    9.6
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">读者评分</div>
                </div>
              </div>
              <Link
                to={`/article/${chapters[0].slug}`}
                className="inline-block px-10 py-3.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                立即开始阅读
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}